import { Suspense, lazy, type ReactNode } from 'react';
import { Navigate, Outlet, createBrowserRouter, redirect, type RouteObject } from 'react-router';

import { AnalyticsTracker } from '@/components/analytics/AnalyticsTracker';
import { ConsentBanner } from '@/components/analytics/ConsentBanner';
import { DashboardMaintenanceGate } from '@/components/DashboardMaintenanceGate';
import { RouteFallback } from '@/components/ui/RouteFallback';
import { RouteError } from '@/routes/RouteError';
import { ScrollToTop } from '@/routes/ScrollToTop';
import { ProtectedRoute } from '@/features/auth/components/ProtectedRoute';
import { REPORTS_PERMISSION } from '@/layouts/navPermissions';
import { SignedOutOnly } from '@/features/auth/components/SignedOutOnly';
import { RequireEntitlement } from '@/features/billing/components/RequireEntitlement';
import { PausedFlagStrip } from '@/features/system/components/RequireFlag';
import { RequireSubscription } from '@/features/billing/components/RequireSubscription';
import { SUBSCRIBE_PATH } from '@/features/billing/lib/planSelection';
import { PREVIEW_ROUTES } from '@/previewThemes/runtime/routes';
import { RequireCustomer } from '@/features/storefront/account/components/RequireCustomer';
import { RequireFeature } from '@/features/storefront/components/RequireFeature';
import {
  LIVE_STORE_ROOT,
  PREVIEW_STORE_ROOT,
  shopOwnHost,
} from '@/features/storefront/storefrontHost';
import { ChangePasswordPage } from '@/features/auth/pages/ChangePasswordPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { RegisterPage } from '@/features/auth/pages/RegisterPage';
import { ResetPasswordPage } from '@/features/auth/pages/ResetPasswordPage';
import { VerifyEmailPage } from '@/features/auth/pages/VerifyEmailPage';
import { AuthLayout } from '@/layouts/AuthLayout';
import { RootLayout } from '@/layouts/RootLayout';
import { HomePage } from '@/pages/HomePage';
import { NotFoundPage } from '@/pages/NotFoundPage';

/**
 * Four surfaces:
 *
 * `/`         marketing site
 * `/login…`   unauthenticated account pages
 * `/app/*`    merchant dashboard, behind ProtectedRoute
 * `/store/*`  the shopper storefront, resolved by hostname on the server
 *             (and `/preview/store/*`, the same shop opened from the dashboard)
 *
 * In production the storefront is served on the merchant's own domain and takes
 * the root path; `/store` is the development and preview route into the same
 * components.
 *
 * The dashboard and storefront are lazily loaded. A shopper on a phone should
 * never download the merchant admin, and vice versa - they are entirely
 * different audiences on entirely different connections.
 */
const DashboardPage = lazy(() =>
  import('@/features/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const SettingsPage = lazy(() =>
  import('@/features/dashboard/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const TeamPage = lazy(() =>
  import('@/features/team/pages/TeamPage').then((m) => ({ default: m.TeamPage })),
);
const MyAccountPage = lazy(() =>
  import('@/features/account/pages/MyAccountPage').then((m) => ({ default: m.MyAccountPage })),
);
const SecurityPage = lazy(() =>
  import('@/features/auth/pages/SecurityPage').then((m) => ({ default: m.SecurityPage })),
);
const MerchantPoliciesPage = lazy(() =>
  import('@/features/legal/pages/MerchantPoliciesPage').then((m) => ({
    default: m.MerchantPoliciesPage,
  })),
);
const PublicPolicyPage = lazy(() =>
  import('@/features/legal/pages/PublicPolicyPage').then((m) => ({
    default: m.PublicPolicyPage,
  })),
);
const PublicPoliciesIndexPage = lazy(() =>
  import('@/features/legal/pages/PublicPolicyPage').then((m) => ({
    default: m.PublicPoliciesIndexPage,
  })),
);
const ProductsPage = lazy(() =>
  import('@/features/catalog/pages/ProductsPage').then((m) => ({ default: m.ProductsPage })),
);
const ProductFormPage = lazy(() =>
  import('@/features/catalog/pages/ProductFormPage').then((m) => ({
    default: m.ProductFormPage,
  })),
);
const CategoriesPage = lazy(() =>
  import('@/features/catalog/pages/CategoriesPage').then((m) => ({ default: m.CategoriesPage })),
);
const GiftCardsPage = lazy(() =>
  import('@/features/giftcards/pages/GiftCardsPage').then((m) => ({ default: m.GiftCardsPage })),
);
const InventoryPage = lazy(() =>
  import('@/features/inventory/pages/InventoryPage').then((m) => ({ default: m.InventoryPage })),
);
const OrdersPage = lazy(() =>
  import('@/features/orders/pages/OrdersPage').then((m) => ({ default: m.OrdersPage })),
);
const MySchedulePage = lazy(() =>
  import('@/features/bookings/pages/MySchedulePage').then((m) => ({ default: m.MySchedulePage })),
);
const BookingsPage = lazy(() =>
  import('@/features/bookings/pages/BookingsPage').then((m) => ({ default: m.BookingsPage })),
);
const ReviewsPage = lazy(() =>
  import('@/features/reviews/pages/ReviewsPage').then((m) => ({ default: m.ReviewsPage })),
);
const QuestionsPage = lazy(() =>
  import('@/features/reviews/pages/QuestionsPage').then((m) => ({ default: m.QuestionsPage })),
);
const InvoicesPage = lazy(() =>
  import('@/features/orders/pages/InvoicesPage').then((m) => ({ default: m.InvoicesPage })),
);
const CustomersPage = lazy(() =>
  import('@/features/customers/pages/CustomersPage').then((m) => ({ default: m.CustomersPage })),
);
const CustomerFormPage = lazy(() =>
  import('@/features/customers/pages/CustomerFormPage').then((m) => ({
    default: m.CustomerFormPage,
  })),
);
const AnalyticsPage = lazy(() =>
  import('@/features/analytics/pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
);
const MerchantReportsPage = lazy(() =>
  import('@/features/reporting/pages/MerchantReportsPage').then((m) => ({
    default: m.MerchantReportsPage,
  })),
);
const MerchantReportPage = lazy(() =>
  import('@/features/reporting/pages/MerchantReportsPage').then((m) => ({
    default: m.MerchantReportPage,
  })),
);
const WhatsAppCampaignsPage = lazy(() =>
  import('@/features/whatsapp/pages/WhatsAppCampaignsPage').then((m) => ({
    default: m.WhatsAppCampaignsPage,
  })),
);
const WhatsAppOverviewPage = lazy(() =>
  import('@/features/whatsapp/pages/WhatsAppOverviewPage').then((m) => ({
    default: m.WhatsAppOverviewPage,
  })),
);
const WhatsAppConnectPage = lazy(() =>
  import('@/features/whatsapp/pages/WhatsAppConnectPage').then((m) => ({
    default: m.WhatsAppConnectPage,
  })),
);
const WhatsAppCatalogPage = lazy(() =>
  import('@/features/whatsapp/pages/WhatsAppCatalogPage').then((m) => ({
    default: m.WhatsAppCatalogPage,
  })),
);
const WhatsAppInboxPage = lazy(() =>
  import('@/features/whatsapp/pages/WhatsAppInboxPage').then((m) => ({
    default: m.WhatsAppInboxPage,
  })),
);
const WhatsAppOrdersPage = lazy(() =>
  import('@/features/whatsapp/pages/WhatsAppOrdersPage').then((m) => ({
    default: m.WhatsAppOrdersPage,
  })),
);
const WhatsAppAutomationsPage = lazy(() =>
  import('@/features/whatsapp/pages/WhatsAppAutomationsPage').then((m) => ({
    default: m.WhatsAppAutomationsPage,
  })),
);
const WhatsAppTemplatesPage = lazy(() =>
  import('@/features/whatsapp/pages/WhatsAppTemplatesPage').then((m) => ({
    default: m.WhatsAppTemplatesPage,
  })),
);
const WhatsAppAnalyticsPage = lazy(() =>
  import('@/features/whatsapp/pages/WhatsAppAnalyticsPage').then((m) => ({
    default: m.WhatsAppAnalyticsPage,
  })),
);
const WhatsAppSettingsPage = lazy(() =>
  import('@/features/whatsapp/pages/WhatsAppSettingsPage').then((m) => ({
    default: m.WhatsAppSettingsPage,
  })),
);
const PlatformWhatsAppPage = lazy(() =>
  import('@/features/platform/pages/PlatformWhatsAppPage').then((m) => ({
    default: m.PlatformWhatsAppPage,
  })),
);
const AIImageStudioPage = lazy(() =>
  import('@/features/aiImages/pages/AIImageStudioPage').then((m) => ({
    default: m.AIImageStudioPage,
  })),
);
const AIImageHistoryPage = lazy(() =>
  import('@/features/aiImages/pages/AIImageHistoryPage').then((m) => ({
    default: m.AIImageHistoryPage,
  })),
);
const DiscountsPage = lazy(() =>
  import('@/features/discounts/pages/DiscountsPage').then((m) => ({ default: m.DiscountsPage })),
);
const DiscountFormPage = lazy(() =>
  import('@/features/discounts/pages/DiscountFormPage').then((m) => ({
    default: m.DiscountFormPage,
  })),
);
const PaymentsPage = lazy(() =>
  import('@/features/store/pages/PaymentsPage').then((m) => ({ default: m.PaymentsPage })),
);
const CheckoutOptionsPage = lazy(() =>
  import('@/features/store/pages/CheckoutOptionsPage').then((m) => ({
    default: m.CheckoutOptionsPage,
  })),
);
const ShippingSettingsPage = lazy(() =>
  import('@/features/shipping/pages/ShippingSettingsPage').then((m) => ({
    default: m.ShippingSettingsPage,
  })),
);
const EmailTemplatesPage = lazy(() =>
  import('@/features/comms/pages/EmailTemplatesPage').then((m) => ({
    default: m.EmailTemplatesPage,
  })),
);
const ShipmentsPage = lazy(() =>
  import('@/features/shipping/pages/ShipmentsPage').then((m) => ({
    default: m.ShipmentsPage,
  })),
);
const DomainPage = lazy(() =>
  import('@/features/store/pages/DomainPage').then((m) => ({ default: m.DomainPage })),
);
const DomainEmailPage = lazy(() =>
  import('@/features/store/pages/DomainEmailPage').then((m) => ({ default: m.DomainEmailPage })),
);
const CreateStorePage = lazy(() =>
  import('@/features/store/pages/CreateStorePage').then((m) => ({ default: m.CreateStorePage })),
);
const StorefrontPage = lazy(() =>
  import('@/features/store/pages/StorefrontPage').then((m) => ({ default: m.StorefrontPage })),
);
const StoreQrCodePage = lazy(() =>
  import('@/features/store/pages/StoreQrCodePage').then((m) => ({ default: m.StoreQrCodePage })),
);
const ThemeCustomizerPage = lazy(() =>
  import('@/features/store/pages/ThemeCustomizerPage').then((m) => ({
    default: m.ThemeCustomizerPage,
  })),
);
const MetaobjectsPage = lazy(() =>
  import('@/features/content/pages/MetaobjectsPage').then((m) => ({ default: m.MetaobjectsPage })),
);
const MetaobjectDefinitionPage = lazy(() =>
  import('@/features/content/pages/MetaobjectDefinitionPage').then((m) => ({
    default: m.MetaobjectDefinitionPage,
  })),
);
const MetaobjectEntryPage = lazy(() =>
  import('@/features/content/pages/MetaobjectEntryPage').then((m) => ({
    default: m.MetaobjectEntryPage,
  })),
);
const MetafieldsPage = lazy(() =>
  import('@/features/content/pages/MetafieldsPage').then((m) => ({ default: m.MetafieldsPage })),
);
const PagesPage = lazy(() =>
  import('@/features/content/pages/PagesPage').then((m) => ({ default: m.PagesPage })),
);
const PageEditorPage = lazy(() =>
  import('@/features/content/pages/PageEditorPage').then((m) => ({
    default: m.PageEditorPage,
  })),
);
const BlogPage = lazy(() =>
  import('@/features/content/pages/BlogPage').then((m) => ({ default: m.BlogPage })),
);
const BlogPostFormPage = lazy(() =>
  import('@/features/content/pages/BlogPostFormPage').then((m) => ({
    default: m.BlogPostFormPage,
  })),
);
const MenusPage = lazy(() =>
  import('@/features/content/pages/MenusPage').then((m) => ({ default: m.MenusPage })),
);
const MenuFormPage = lazy(() =>
  import('@/features/content/pages/MenuFormPage').then((m) => ({ default: m.MenuFormPage })),
);
const TranslationsPage = lazy(() =>
  import('@/features/i18n/pages/TranslationsPage').then((m) => ({ default: m.TranslationsPage })),
);
const TranslationEditorPage = lazy(() =>
  import('@/features/i18n/pages/TranslationEditorPage').then((m) => ({
    default: m.TranslationEditorPage,
  })),
);
const LanguageSettingsPage = lazy(() =>
  import('@/features/i18n/pages/LanguageSettingsPage').then((m) => ({
    default: m.LanguageSettingsPage,
  })),
);
const SignatureSettingsPage = lazy(() =>
  import('@/features/signature/pages/SignatureSettingsPage').then((m) => ({
    default: m.SignatureSettingsPage,
  })),
);
const SupportChatPage = lazy(() =>
  import('@/features/supportChat/pages/SupportChatPage').then((m) => ({
    default: m.SupportChatPage,
  })),
);
const SetupAssistancePage = lazy(() =>
  import('@/features/setupAssistance/pages/SetupAssistancePage').then((m) => ({
    default: m.SetupAssistancePage,
  })),
);
const BillingPage = lazy(() =>
  import('@/features/billing/pages/BillingPage').then((m) => ({ default: m.BillingPage })),
);
const SubscribePage = lazy(() =>
  import('@/features/billing/pages/SubscribePage').then((m) => ({
    default: m.SubscribePage,
  })),
);
const OrderRequestsPage = lazy(() =>
  import('@/features/orders/pages/OrderRequestsPage').then((m) => ({
    default: m.OrderRequestsPage,
  })),
);
const OrderPolicyPage = lazy(() =>
  import('@/features/store/pages/OrderPolicyPage').then((m) => ({
    default: m.OrderPolicyPage,
  })),
);
const StoreLocationsPage = lazy(() =>
  import('@/features/storeLocations/StoreLocationsPage').then((m) => ({
    default: m.StoreLocationsPage,
  })),
);
const OurTeamPage = lazy(() =>
  import('@/features/ourTeam/OurTeamPage').then((m) => ({
    default: m.OurTeamPage,
  })),
);
const BookingServicesPage = lazy(() =>
  import('@/features/bookingServices/BookingServicesPage').then((m) => ({
    default: m.BookingServicesPage,
  })),
);
const BookingPackagesPage = lazy(() =>
  import('@/features/bookingPackages/BookingPackagesPage').then((m) => ({
    default: m.BookingPackagesPage,
  })),
);
const FaqsPage = lazy(() =>
  import('@/features/faqs/FaqsPage').then((m) => ({
    default: m.FaqsPage,
  })),
);
const CareersPage = lazy(() =>
  import('@/features/careers/CareersPage').then((m) => ({
    default: m.CareersPage,
  })),
);
const ApplicationsPage = lazy(() =>
  import('@/features/careers/ApplicationsPage').then((m) => ({
    default: m.ApplicationsPage,
  })),
);
const ApplicationDetailPage = lazy(() =>
  import('@/features/careers/ApplicationDetailPage').then((m) => ({
    default: m.ApplicationDetailPage,
  })),
);
const PricingPlansPage = lazy(() =>
  import('@/features/pricingPlans/PricingPlansPage').then((m) => ({
    default: m.PricingPlansPage,
  })),
);
const OrderDetailPage = lazy(() =>
  import('@/features/orders/pages/OrderDetailPage').then((m) => ({
    default: m.OrderDetailPage,
  })),
);
const CreateOrderPage = lazy(() =>
  import('@/features/orders/pages/CreateOrderPage').then((m) => ({
    default: m.CreateOrderPage,
  })),
);
const AppLayout = lazy(() =>
  import('@/layouts/AppLayout').then((m) => ({ default: m.AppLayout })),
);
const CreateBusinessPage = lazy(() =>
  import('@/features/onboarding/pages/CreateBusinessPage').then((m) => ({
    default: m.CreateBusinessPage,
  })),
);
const PlatformStatusPage = lazy(() =>
  import('@/features/platform/pages/PlatformStatusPage').then((m) => ({
    default: m.PlatformStatusPage,
  })),
);
const StorefrontLayout = lazy(() =>
  import('@/layouts/StorefrontLayout').then((m) => ({ default: m.StorefrontLayout })),
);
const PlatformLayout = lazy(() =>
  import('@/layouts/PlatformLayout').then((m) => ({ default: m.PlatformLayout })),
);
const PartnersPage = lazy(() =>
  import('@/features/platform/pages/PartnersPage').then((m) => ({ default: m.PartnersPage })),
);
const PlatformDealsPage = lazy(() =>
  import('@/features/platform/pages/PlatformDealsPage').then((m) => ({
    default: m.PlatformDealsPage,
  })),
);
const PartnerDetailPage = lazy(() =>
  import('@/features/platform/pages/PartnerDetailPage').then((m) => ({
    default: m.PartnerDetailPage,
  })),
);
const PartnerCommissionsPage = lazy(() =>
  import('@/features/platform/pages/PartnerCommissionsPage').then((m) => ({
    default: m.PartnerCommissionsPage,
  })),
);
const CommissionMatrixPage = lazy(() =>
  import('@/features/platform/pages/CommissionMatrixPage').then((m) => ({
    default: m.CommissionMatrixPage,
  })),
);
const PartnerCommissionPage = lazy(() =>
  import('@/features/partners/pages/PartnerCommissionPage').then((m) => ({
    default: m.PartnerCommissionPage,
  })),
);
const PartnerLayout = lazy(() =>
  import('@/layouts/PartnerLayout').then((m) => ({ default: m.PartnerLayout })),
);
const BecomePartnerPage = lazy(() =>
  import('@/features/partners/pages/BecomePartnerPage').then((m) => ({
    default: m.BecomePartnerPage,
  })),
);
const PartnerDashboardPage = lazy(() =>
  import('@/features/partners/pages/PartnerDashboardPage').then((m) => ({
    default: m.PartnerDashboardPage,
  })),
);
const PartnerReferralsPage = lazy(() =>
  import('@/features/partners/pages/PartnerReferralsPage').then((m) => ({
    default: m.PartnerReferralsPage,
  })),
);
const PartnerEarningsPage = lazy(() =>
  import('@/features/partners/pages/PartnerEarningsPage').then((m) => ({
    default: m.PartnerEarningsPage,
  })),
);
const PartnerProfilePage = lazy(() =>
  import('@/features/partners/pages/PartnerProfilePage').then((m) => ({
    default: m.PartnerProfilePage,
  })),
);
const PlatformOverviewPage = lazy(() =>
  import('@/features/platform/pages/PlatformOverviewPage').then((m) => ({
    default: m.PlatformOverviewPage,
  })),
);
const PlatformReportsPage = lazy(() =>
  import('@/features/reporting/pages/PlatformReportsPage').then((m) => ({
    default: m.PlatformReportsPage,
  })),
);
const PlatformReportPage = lazy(() =>
  import('@/features/reporting/pages/PlatformReportsPage').then((m) => ({
    default: m.PlatformReportPage,
  })),
);
const MerchantsPage = lazy(() =>
  import('@/features/platform/pages/MerchantsPage').then((m) => ({ default: m.MerchantsPage })),
);
const MerchantDetailPage = lazy(() =>
  import('@/features/platform/pages/MerchantDetailPage').then((m) => ({
    default: m.MerchantDetailPage,
  })),
);
const SupportChatInboxPage = lazy(() =>
  import('@/features/platform/supportChat/SupportChatInboxPage').then((m) => ({
    default: m.SupportChatInboxPage,
  })),
);
const SetupAssistanceQueuePage = lazy(() =>
  import('@/features/platform/pages/SetupAssistanceQueuePage').then((m) => ({
    default: m.SetupAssistanceQueuePage,
  })),
);
const SetupAssistanceUsagePage = lazy(() =>
  import('@/features/platform/pages/SetupAssistanceUsagePage').then((m) => ({
    default: m.SetupAssistanceUsagePage,
  })),
);
const PlansPage = lazy(() =>
  import('@/features/platform/pages/PlansPage').then((m) => ({ default: m.PlansPage })),
);
const PlatformStaffPage = lazy(() =>
  import('@/features/platform/pages/PlatformStaffPage').then((m) => ({
    default: m.PlatformStaffPage,
  })),
);
const PlatformAuditPage = lazy(() =>
  import('@/features/platform/pages/PlatformAuditPage').then((m) => ({
    default: m.PlatformAuditPage,
  })),
);
const PlatformSupportSessionsPage = lazy(() =>
  import('@/features/platform/pages/PlatformSupportSessionsPage').then((m) => ({
    default: m.PlatformSupportSessionsPage,
  })),
);
const PlatformOpsPage = lazy(() =>
  import('@/features/platform/pages/PlatformOpsPage').then((m) => ({
    default: m.PlatformOpsPage,
  })),
);
const PlatformSystemLogsPage = lazy(() =>
  import('@/features/platform/pages/PlatformSystemLogsPage').then((m) => ({
    default: m.PlatformSystemLogsPage,
  })),
);
const PlatformLogLevelsPage = lazy(() =>
  import('@/features/platform/pages/PlatformLogLevelsPage').then((m) => ({
    default: m.PlatformLogLevelsPage,
  })),
);
const PlatformEmailsPage = lazy(() =>
  import('@/features/platform/pages/PlatformEmailsPage').then((m) => ({
    default: m.PlatformEmailsPage,
  })),
);
const PlatformSignaturePage = lazy(() =>
  import('@/features/platform/pages/PlatformSignaturePage').then((m) => ({
    default: m.PlatformSignaturePage,
  })),
);
const PlatformPoliciesPage = lazy(() =>
  import('@/features/platform/pages/PlatformPoliciesPage').then((m) => ({
    default: m.PlatformPoliciesPage,
  })),
);
const PlatformPolicyDetailPage = lazy(() =>
  import('@/features/platform/pages/PlatformPolicyDetailPage').then((m) => ({
    default: m.PlatformPolicyDetailPage,
  })),
);
const PlatformIntegrationsPage = lazy(() =>
  import('@/features/platform/pages/PlatformIntegrationsPage').then((m) => ({
    default: m.PlatformIntegrationsPage,
  })),
);
const PlatformExpensesPage = lazy(() =>
  import('@/features/platform/pages/PlatformExpensesPage').then((m) => ({
    default: m.PlatformExpensesPage,
  })),
);
const PlatformSalesInquiriesPage = lazy(() =>
  import('@/features/platform/pages/PlatformSalesInquiriesPage').then((m) => ({
    default: m.PlatformSalesInquiriesPage,
  })),
);
const PlatformSiteContentPage = lazy(() =>
  import('@/features/platform/pages/PlatformSiteContentPage').then((m) => ({
    default: m.PlatformSiteContentPage,
  })),
);
const PlatformSiteVisitorsPage = lazy(() =>
  import('@/features/platform/pages/PlatformSiteVisitorsPage').then((m) => ({
    default: m.PlatformSiteVisitorsPage,
  })),
);
const PlatformIndustryAssetsPage = lazy(() =>
  import('@/features/platform/pages/PlatformIndustryAssetsPage').then((m) => ({
    default: m.PlatformIndustryAssetsPage,
  })),
);
const PlatformProductMasterPage = lazy(() =>
  import('@/features/platform/pages/PlatformProductMasterPage').then((m) => ({
    default: m.PlatformProductMasterPage,
  })),
);
const PlatformThemeCustomizerPage = lazy(() =>
  import('@/features/platform/pages/PlatformThemeCustomizerPage').then((m) => ({
    default: m.PlatformThemeCustomizerPage,
  })),
);
const PlatformThemesPage = lazy(() =>
  import('@/features/platform/pages/PlatformThemesPage').then((m) => ({
    default: m.PlatformThemesPage,
  })),
);
const PlatformLanguagesPage = lazy(() =>
  import('@/features/platform/pages/PlatformLanguagesPage').then((m) => ({
    default: m.PlatformLanguagesPage,
  })),
);
const PlatformFlagsPage = lazy(() =>
  import('@/features/platform/pages/PlatformFlagsPage').then((m) => ({
    default: m.PlatformFlagsPage,
  })),
);
const PlatformAIExpensesPage = lazy(() =>
  import('@/features/platform/pages/PlatformAIExpensesPage').then((m) => ({
    default: m.PlatformAIExpensesPage,
  })),
);
const PlatformShippingPage = lazy(() =>
  import('@/features/platform/pages/PlatformShippingPage').then((m) => ({
    default: m.PlatformShippingPage,
  })),
);
const PlatformDomainsPage = lazy(() =>
  import('@/features/platform/pages/PlatformDomainsPage').then((m) => ({
    default: m.PlatformDomainsPage,
  })),
);
const PlatformWebhooksPage = lazy(() =>
  import('@/features/platform/pages/PlatformWebhooksPage').then((m) => ({
    default: m.PlatformWebhooksPage,
  })),
);
const PlatformOrdersPage = lazy(() =>
  import('@/features/platform/pages/PlatformOrdersPage').then((m) => ({
    default: m.PlatformOrdersPage,
  })),
);
const ModerationPage = lazy(() =>
  import('@/features/platform/pages/ModerationPage').then((m) => ({ default: m.ModerationPage })),
);
const PlatformUsersPage = lazy(() =>
  import('@/features/platform/pages/PlatformUsersPage').then((m) => ({
    default: m.PlatformUsersPage,
  })),
);
const WhyShopLaunchPage = lazy(() =>
  import('@/features/marketing/pages/WhyShopLaunchPage').then((m) => ({
    default: m.WhyShopLaunchPage,
  })),
);
const PricingPage = lazy(() =>
  import('@/features/marketing/pages/PricingPage').then((m) => ({ default: m.PricingPage })),
);
const TodaysDealsPage = lazy(() =>
  import('@/features/marketing/pages/TodaysDealsPage').then((m) => ({
    default: m.TodaysDealsPage,
  })),
);
const SharedPlanPage = lazy(() =>
  import('@/features/marketing/pages/SharedPlanPage').then((m) => ({
    default: m.SharedPlanPage,
  })),
);
const EnterprisePage = lazy(() =>
  import('@/features/marketing/pages/EnterprisePage').then((m) => ({
    default: m.EnterprisePage,
  })),
);
const TalkToSalesPage = lazy(() =>
  import('@/features/marketing/pages/TalkToSalesPage').then((m) => ({
    default: m.TalkToSalesPage,
  })),
);
const FeaturePage = lazy(() =>
  import('@/features/marketing/pages/FeaturePage').then((m) => ({ default: m.FeaturePage })),
);
const HelpCentrePage = lazy(() =>
  import('@/features/marketing/pages/HelpCentrePage').then((m) => ({
    default: m.HelpCentrePage,
  })),
);
const StorefrontHomePage = lazy(() =>
  import('@/features/storefront/pages/StorefrontHomePage').then((m) => ({
    default: m.StorefrontHomePage,
  })),
);
const StorefrontProductPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontProductPage').then((m) => ({
    default: m.StorefrontProductPage,
  })),
);
const StorefrontProductsPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontProductsPage').then((m) => ({
    default: m.StorefrontProductsPage,
  })),
);
const StorefrontCategoriesPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontCategoriesPage').then((m) => ({
    default: m.StorefrontCategoriesPage,
  })),
);
const OrderConfirmationPage = lazy(() =>
  import('@/features/storefront/pages/OrderConfirmationPage').then((m) => ({
    default: m.OrderConfirmationPage,
  })),
);
const OrderPayPage = lazy(() =>
  import('@/features/storefront/pages/OrderPayPage').then((m) => ({ default: m.OrderPayPage })),
);
const StorefrontSearchPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontSearchPage').then((m) => ({
    default: m.StorefrontSearchPage,
  })),
);
const StorefrontNotFoundPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontNotFoundPage').then((m) => ({
    default: m.StorefrontNotFoundPage,
  })),
);
const StorefrontPageView = lazy(() =>
  import('@/features/storefront/pages/StorefrontPageView').then((m) => ({
    default: m.StorefrontPageView,
  })),
);
const StorefrontPolicyPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontPolicyPage').then((m) => ({
    default: m.StorefrontPolicyPage,
  })),
);
const StorefrontBlogPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontBlogPage').then((m) => ({
    default: m.StorefrontBlogPage,
  })),
);
const StorefrontPostPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontPostPage').then((m) => ({
    default: m.StorefrontPostPage,
  })),
);
const StorefrontTeamMemberPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontTeamMemberPage').then((m) => ({
    default: m.StorefrontTeamMemberPage,
  })),
);
const StorefrontCareersPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontCareersPage').then((m) => ({
    default: m.StorefrontCareersPage,
  })),
);
const StorefrontCareerPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontCareerPage').then((m) => ({
    default: m.StorefrontCareerPage,
  })),
);
const StorefrontQueuePage = lazy(() =>
  import('@/features/storefront/pages/StorefrontQueuePage').then((m) => ({
    default: m.StorefrontQueuePage,
  })),
);
const CartPage = lazy(() =>
  import('@/features/storefront/pages/CartPage').then((m) => ({ default: m.CartPage })),
);
const StorefrontWishlistPage = lazy(() =>
  import('@/features/storefront/pages/StorefrontWishlistPage').then((m) => ({
    default: m.StorefrontWishlistPage,
  })),
);
const CheckoutPage = lazy(() =>
  import('@/features/storefront/pages/CheckoutPage').then((m) => ({ default: m.CheckoutPage })),
);
const CustomerLoginPage = lazy(() =>
  import('@/features/storefront/account/pages/CustomerLoginPage').then((m) => ({
    default: m.CustomerLoginPage,
  })),
);
const CustomerRegisterPage = lazy(() =>
  import('@/features/storefront/account/pages/CustomerRegisterPage').then((m) => ({
    default: m.CustomerRegisterPage,
  })),
);
const CustomerForgotPasswordPage = lazy(() =>
  import('@/features/storefront/account/pages/CustomerForgotPasswordPage').then((m) => ({
    default: m.CustomerForgotPasswordPage,
  })),
);
const CustomerResetPasswordPage = lazy(() =>
  import('@/features/storefront/account/pages/CustomerResetPasswordPage').then((m) => ({
    default: m.CustomerResetPasswordPage,
  })),
);
const CustomerVerifyEmailPage = lazy(() =>
  import('@/features/storefront/account/pages/CustomerVerifyEmailPage').then((m) => ({
    default: m.CustomerVerifyEmailPage,
  })),
);
const AccountLayout = lazy(() =>
  import('@/features/storefront/account/pages/AccountLayout').then((m) => ({
    default: m.AccountLayout,
  })),
);
const AccountOverviewPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountOverviewPage').then((m) => ({
    default: m.AccountOverviewPage,
  })),
);
const AccountOrdersPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountOrdersPage').then((m) => ({
    default: m.AccountOrdersPage,
  })),
);
const AccountBookingsPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountBookingsPage').then((m) => ({
    default: m.AccountBookingsPage,
  })),
);
const AccountPackagesPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountPackagesPage').then((m) => ({
    default: m.AccountPackagesPage,
  })),
);
const AccountOrderDetailPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountOrderDetailPage').then((m) => ({
    default: m.AccountOrderDetailPage,
  })),
);
const AccountAddressesPage = lazy(() =>
  import('@/features/storefront/account/pages/AccountAddressesPage').then((m) => ({
    default: m.AccountAddressesPage,
  })),
);
const AccountProfilePage = lazy(() =>
  import('@/features/storefront/account/pages/AccountProfilePage').then((m) => ({
    default: m.AccountProfilePage,
  })),
);

/**
 * `/app/billing/checkout` was where subscription checkout used to live. It is
 * its own screen at `/subscribe` now, outside the dashboard chrome; the old
 * path forwards, query and all, so a bookmark or a documented link still works.
 * A loader rather than a component, so the redirect happens before anything of
 * the dashboard is rendered on the way past.
 */
function subscribeRedirect({ request }: { request: Request }) {
  return redirect(`${SUBSCRIBE_PATH}${new URL(request.url).search}`);
}

/**
 * `metro-clinic.neoncarts.com/` (or the shop's own `www.metroclinic.in/`) is
 * the shop, not NeonCarts' marketing home, so a shop's own address goes
 * straight to its storefront.
 */
function storeHomeRedirect({ request }: { request: Request }) {
  return shopOwnHost() ? redirect(`/store${new URL(request.url).search}`) : null;
}

/**
 * Account emails sent before their links carried the `/store` mount pointed at
 * `{shop}/account/...`, which the storefront does not answer on. Those links
 * are still sitting in inboxes, so on a shop's own address they forward into
 * the storefront, token and all. Anywhere else this is an ordinary 404.
 */
function storeAccountRedirect({ request }: { request: Request }) {
  const url = new URL(request.url);
  return shopOwnHost() ? redirect(`/store${url.pathname}${url.search}`) : null;
}

const lazily = (element: ReactNode) => (
  <Suspense fallback={<RouteFallback />}>{element}</Suspense>
);

const ROUTES: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, loader: storeHomeRedirect, element: <HomePage /> },
      { path: 'why-shoplaunch', element: lazily(<WhyShopLaunchPage />) },
      { path: 'pricing', element: lazily(<PricingPage />) },
      { path: 'deals', element: lazily(<TodaysDealsPage />) },
      { path: 'enterprise', element: lazily(<EnterprisePage />) },
      // A plan's features through the share link on `/platform/plans`. The
      // token is the credential, so it asks for no session.
      { path: 'plan-offer/:planId/:token', element: lazily(<SharedPlanPage />) },
      { path: 'talk-to-sales', element: lazily(<TalkToSalesPage />) },
      { path: 'help', element: lazily(<HelpCentrePage />) },
      { path: 'features/:slug', element: lazily(<FeaturePage />) },
      // Once the public status page, now the platform owner's (see
      // `/platform/status`). Old links and bookmarks land on the deals instead
      // of a 404.
      { path: 'status', element: <Navigate to="/deals" replace /> },
      // NeonCarts' own published policies. Public: the registration form
      // links here before the reader has an account.
      { path: 'legal', element: lazily(<PublicPoliciesIndexPage />) },
      { path: 'legal/:slug', element: lazily(<PublicPolicyPage />) },
      { path: 'become-a-partner', element: lazily(<BecomePartnerPage />) },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      {
        // The two forms a signed-in merchant has no further use for. The rest of
        // this layout is reached by a link in an email or by a caller who is
        // already signed in, so none of it belongs behind this.
        element: <SignedOutOnly />,
        children: [
          { path: 'login', element: <LoginPage /> },
          { path: 'register', element: <RegisterPage /> },
        ],
      },
      { path: 'verify-email', element: <VerifyEmailPage /> },
      { path: 'forgot-password', element: <ForgotPasswordPage /> },
      { path: 'reset-password', element: <ResetPasswordPage /> },
      { path: 'accept-invite', element: <ResetPasswordPage mode="invite" /> },
      // Signed in, unlike its neighbours, and deliberately in this layout: a
      // caller who must change their password cannot load the dashboard, so the
      // form has to live somewhere that fetches nothing. It guards itself rather
      // than sitting behind ProtectedRoute, which would bounce it straight back
      // here.
      { path: 'change-password', element: <ChangePasswordPage /> },
    ],
  },
  {
    // Signed in, but the business may not exist yet - so no business required.
    element: (
      <DashboardMaintenanceGate>
        <ProtectedRoute requireBusiness={false} />
      </DashboardMaintenanceGate>
    ),
    children: [{ path: 'onboarding', element: lazily(<CreateBusinessPage />) }],
  },
  {
    // The payment step of signing up. Signed in and with a business - a
    // subscription belongs to one - but outside the dashboard chrome, because
    // at this point there is exactly one thing to decide.
    element: (
      <DashboardMaintenanceGate>
        <ProtectedRoute />
      </DashboardMaintenanceGate>
    ),
    children: [{ path: 'subscribe', element: lazily(<SubscribePage />) }],
  },
  {
    // Full maintenance is caught here, above every guard that fetches: the
    // plan check would otherwise show its own "could not check your plan"
    // before the dashboard layout ever mounted.
    element: (
      <DashboardMaintenanceGate>
        <ProtectedRoute />
      </DashboardMaintenanceGate>
    ),
    children: [
      {
        path: 'app',
        // Above the layout, so a merchant who has not settled a plan is sent
        // back to `/subscribe` before any of the dashboard renders or fetches.
        element: <RequireSubscription />,
        children: [
          {
            element: lazily(<AppLayout />),
            // Each area is guarded by the same permission the API enforces, so the
            // sidebar, the route and the endpoint agree on who may be where.
            children: [
              { index: true, element: lazily(<DashboardPage />) },
              {
                element: <ProtectedRoute permission="PRODUCT_READ" />,
                children: [
                  { path: 'catalog', element: lazily(<ProductsPage />) },
                  { path: 'catalog/new', element: lazily(<ProductFormPage />) },
                  { path: 'catalog/:productId', element: lazily(<ProductFormPage />) },
                  // Deliberately not under `catalog/`, where it would both compete
                  // with `catalog/:productId` and keep the Products nav item lit.
                  { path: 'categories', element: lazily(<CategoriesPage />) },
                  {
                    element: <RequireEntitlement feature="GIFT_CARDS" label="Gift cards" />,
                    children: [{ path: 'gift-cards', element: lazily(<GiftCardsPage />) }],
                  },
                  {
                    // Reading the queue is PRODUCT_READ; the page itself hides
                    // the moderation verbs from anyone without PRODUCT_UPDATE.
                    element: <RequireEntitlement feature="PRODUCT_REVIEWS" label="Product reviews" />,
                    children: [
                      { path: 'reviews', element: lazily(<ReviewsPage />) },
                      // Shoppers' product questions ride on the same plan switch.
                      { path: 'questions', element: lazily(<QuestionsPage />) },
                    ],
                  },
                ],
              },
              {
                element: <ProtectedRoute permission="INVENTORY_READ" />,
                children: [
                  {
                    element: <RequireEntitlement feature="INVENTORY_TRACKING" label="Inventory" />,
                    children: [{ path: 'inventory', element: lazily(<InventoryPage />) }],
                  },
                ],
              },
              {
                element: <ProtectedRoute permission="ORDER_READ" />,
                children: [
                  { path: 'orders', element: lazily(<OrdersPage />) },
                  // Before `orders/:orderId` in spirit: the static segment wins the
                  // match, so `/app/orders/new` never resolves as an order id.
                  {
                    element: <RequireEntitlement feature="MANUAL_ORDERS" label="Manual orders" />,
                    children: [
                      { path: 'orders/new', element: lazily(<CreateOrderPage />) },
                    ],
                  },
                  { path: 'orders/:orderId', element: lazily(<OrderDetailPage />) },
                  // Cancellations, returns, refunds and replacements across
                  // every order: the order clerk's queue.
                  { path: 'order-requests', element: lazily(<OrderRequestsPage />) },
                  // Parcels are the order clerk's job, so they share the order
                  // book's permission rather than the store's.
                  { path: 'shipments', element: lazily(<ShipmentsPage />) },
                  {
                    element: <RequireEntitlement feature="GST_INVOICES" label="GST invoices" />,
                    children: [
                      { path: 'invoices', element: lazily(<InvoicesPage />) },
                    ],
                  },
                ],
              },
              {
                // Its own permission rather than ORDER_READ: a clinic's
                // receptionist works the diary all day and has no business in the
                // order book, and a shop that takes no appointments should not be
                // handing out a grant over a diary it does not keep.
                // Or BOOKING_OWN: the doctor who signs in to see their own.
                element: <ProtectedRoute permission="BOOKING_READ|BOOKING_OWN" />,
                children: [
                  {
                    element: <RequireEntitlement feature="BOOKINGS" label="Appointments & bookings" />,
                    children: [{ path: 'bookings', element: lazily(<BookingsPage />) }],
                  },
                ],
              },
              {
                // A person's own working hours, and nothing else of the team page;
                // or anybody's, and their time off, for the owner and the front desk.
                element: <ProtectedRoute permission="SCHEDULE_OWN|BOOKING_UPDATE|STORE_UPDATE" />,
                children: [
                  {
                    element: <RequireEntitlement feature="BOOKINGS" label="Appointments & bookings" />,
                    children: [{ path: 'my-schedule', element: lazily(<MySchedulePage />) }],
                  },
                ],
              },
              {
                element: <ProtectedRoute permission="CUSTOMER_READ" />,
                children: [
                  { path: 'customers', element: lazily(<CustomersPage />) },
                  { path: 'customers/new', element: lazily(<CustomerFormPage />) },
                  { path: 'customers/:customerId', element: lazily(<CustomerFormPage />) },
                ],
              },
              {
                element: <ProtectedRoute permission="ANALYTICS_READ" />,
                children: [
                  {
                    element: <RequireEntitlement feature="SALES_DASHBOARD" label="Analytics" />,
                    children: [{ path: 'analytics', element: lazily(<AnalyticsPage />) }],
                  },
                ],
              },
              // Reports are deliberately outside both guards above. The catalogue
              // is filtered by the caller's own permissions and each report carries
              // its own - so the order book is reachable by somebody with
              // ORDER_READ and no analytics grant, which is exactly the clerk this
              // exists for. The guard here only turns away somebody who holds none
              // of the reports' permissions, for whom the catalogue would be empty.
              // The entitlement, where a report has one, is enforced on the API,
              // which is the only place it can name the plan to upgrade to.
              {
                element: <ProtectedRoute permission={REPORTS_PERMISSION} />,
                children: [
                  {
                    path: 'reports',
                    children: [
                      { index: true, element: lazily(<MerchantReportsPage />) },
                      { path: ':reportKey', element: lazily(<MerchantReportPage />) },
                    ],
                  },
                ],
              },
              // The AI Image Studio. Gated on AI_USE rather than a catalogue
              // permission: it spends the merchant's metered allowance, which is a
              // different thing to trust somebody with than editing a product - and
              // gated again on the plan, so a merchant whose plan withholds it sees
              // the upgrade path rather than a form that ends in a 402.
              {
                element: <ProtectedRoute permission="AI_USE" />,
                children: [
                  {
                    element: <RequireEntitlement feature="AI_IMAGE_STUDIO" label="AI Image Studio" flag="AI_GENERATION" />,
                    children: [
                      { path: 'ai-images', element: lazily(<AIImageStudioPage />) },
                      { path: 'ai-images/history', element: lazily(<AIImageHistoryPage />) },
                    ],
                  },
                ],
              },
              {
                // WhatsApp as a sales channel. The plan wall covers the channel;
                // the inbox, automations and analytics each sit behind their own
                // sub-feature so a plan that has the channel but not the inbox
                // shows what upgrading adds rather than a 402.
                element: <ProtectedRoute permission="WHATSAPP_VIEW" />,
                children: [
                  {
                    element: <RequireEntitlement feature="WHATSAPP_COMMERCE" label="WhatsApp Commerce" flag="WHATSAPP_COMMERCE" />,
                    children: [
                      { path: 'whatsapp', element: <Navigate to="/app/whatsapp/overview" replace /> },
                      { path: 'whatsapp/overview', element: lazily(<WhatsAppOverviewPage />) },
                      { path: 'whatsapp/connect', element: lazily(<WhatsAppConnectPage />) },
                      { path: 'whatsapp/catalog', element: lazily(<WhatsAppCatalogPage />) },
                      { path: 'whatsapp/orders', element: lazily(<WhatsAppOrdersPage />) },
                      { path: 'whatsapp/templates', element: lazily(<WhatsAppTemplatesPage />) },
                      { path: 'whatsapp/settings', element: lazily(<WhatsAppSettingsPage />) },
                      {
                        element: <RequireEntitlement feature="WHATSAPP_INBOX" label="WhatsApp inbox" />,
                        children: [
                          { path: 'whatsapp/inbox', element: lazily(<WhatsAppInboxPage />) },
                          {
                            path: 'whatsapp/inbox/:conversationId',
                            element: lazily(<WhatsAppInboxPage />),
                          },
                        ],
                      },
                      {
                        element: (
                          <RequireEntitlement
                            feature="WHATSAPP_AUTOMATIONS"
                            label="WhatsApp automations"
                          />
                        ),
                        children: [
                          { path: 'whatsapp/automations', element: lazily(<WhatsAppAutomationsPage />) },
                        ],
                      },
                      {
                        element: (
                          <RequireEntitlement feature="WHATSAPP_ANALYTICS" label="WhatsApp analytics" />
                        ),
                        children: [
                          { path: 'whatsapp/analytics', element: lazily(<WhatsAppAnalyticsPage />) },
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                element: <ProtectedRoute permission="MARKETING_READ" />,
                children: [
                  {
                    element: <RequireEntitlement feature="WHATSAPP_INTEGRATION" label="WhatsApp" flag="WHATSAPP_SENDING" />,
                    children: [
                      { path: 'whatsapp/campaigns', element: lazily(<WhatsAppCampaignsPage />) },
                    ],
                  },
                  {
                    element: <RequireEntitlement feature="DISCOUNTS" label="Discounts & coupons" />,
                    children: [
                      { path: 'discounts', element: lazily(<DiscountsPage />) },
                      // Before `discounts/:discountId` in spirit: the static segment
                      // wins the match, so `/app/discounts/new` never resolves as an id.
                      { path: 'discounts/new', element: lazily(<DiscountFormPage />) },
                      { path: 'discounts/:discountId', element: lazily(<DiscountFormPage />) },
                    ],
                  },
                ],
              },
              {
                element: <ProtectedRoute permission="STORE_READ" />,
                children: [
                  {
                    element: <RequireEntitlement feature="ONLINE_STOREFRONT" label="Online storefront" />,
                    children: [
                      { path: 'storefront', element: lazily(<StorefrontPage />) },
                      // The one screen that exists before a store does. It redirects to
                      // `storefront` once there is one, so the dashboard checklist can
                      // point here whether or not the step is already done.
                      { path: 'storefront/new', element: lazily(<CreateStorePage />) },
                      // Kill switches that cover only part of these pages - buying a
                      // domain, checking DNS, setting up mail - show a strip above
                      // them rather than a wall: the rest of the page still works.
                      {
                        element: (
                          <PausedFlagStrip
                            flags={['DOMAIN_VERIFICATION', 'DOMAIN_PURCHASE', 'EMAIL_HOSTING']}
                          />
                        ),
                        children: [{ path: 'storefront/domain', element: lazily(<DomainPage />) }],
                      },
                      // Business email for one domain. A page of its own because
                      // it is where the merchant is redirected back to after
                      // signing in at Microsoft, and because setting it up has
                      // waiting in the middle - somewhere to come back to.
                      {
                        element: <PausedFlagStrip flags={['EMAIL_HOSTING']} />,
                        children: [
                          {
                            path: 'storefront/domain/:domainId/email',
                            element: lazily(<DomainEmailPage />),
                          },
                        ],
                      },
                      // A screen of its own rather than a card on the storefront page:
                      // the merchant who prints the posters opens this and nothing
                      // else, and a printable thing wants an address it can be sent to.
                      {
                        element: <RequireEntitlement feature="INSTALLABLE_STOREFRONT" label="Installable shop & QR code" />,
                        children: [
                          { path: 'storefront/qr-code', element: lazily(<StoreQrCodePage />) },
                        ],
                      },
                      // The page builder gets a route of its own rather than a dialog:
                      // it is a workspace a merchant spends an hour in, and one they
                      // expect to be able to bookmark, reload and come back to.
                      {
                        path: 'storefront/themes/:themeId',
                        element: lazily(<ThemeCustomizerPage />),
                      },
                    ],
                  },
                  {
                    element: <RequireEntitlement feature="ONLINE_PAYMENTS" label="Online payments" />,
                    children: [
                      { path: 'payments', element: lazily(<PaymentsPage />) },
                    ],
                  },
                  // Beside Payments rather than under it: the keys are a credential
                  // and the options are a storefront setting, and a merchant with
                  // no gateway at all still chooses between cash and WhatsApp.
                  {
                    element: <RequireEntitlement feature="ONLINE_CHECKOUT" label="Checkout" />,
                    children: [
                      { path: 'checkout-options', element: lazily(<CheckoutOptionsPage />) },
                    ],
                  },
                  // What a shopper may ask for after checkout - a store setting
                  // like the checkout options, open to every plan.
                  { path: 'order-policy', element: lazily(<OrderPolicyPage />) },
                  // The shops a shopper can walk into, behind the storefront's
                  // store locator section. A store setting open to every plan.
                  { path: 'store-locations', element: lazily(<StoreLocationsPage />) },
                  // The people a shopper meets and books with - doctors,
                  // stylists. A store setting like the locations above, but only
                  // worth anything to a shop that takes appointments, so it is
                  // behind the same plan feature as the diary. Not `team`: that
                  // is who may sign in to this dashboard.
                  {
                    element: <RequireEntitlement feature="BOOKINGS" label="Appointments & bookings" />,
                    children: [{ path: 'our-team', element: lazily(<OurTeamPage />) }],
                  },
                  // The services a shopper books - what the storefront's bookings
                  // section lists. The store keeps them rather than the theme, so
                  // they outlive a theme switch. Behind the same plan feature as
                  // the diary and Our team.
                  {
                    element: <RequireEntitlement feature="BOOKINGS" label="Appointments & bookings" />,
                    children: [{ path: 'services', element: lazily(<BookingServicesPage />) }],
                  },
                  // Session packages ("5 physio sessions") sold against those
                  // services. Behind the same plan feature as the services; the
                  // page itself says when selling them needs booking payments.
                  {
                    element: <RequireEntitlement feature="BOOKINGS" label="Appointments & bookings" />,
                    children: [{ path: 'packages', element: lazily(<BookingPackagesPage />) }],
                  },
                  // The pricing section's plans, kept by the store for the same
                  // reason. Open to every plan: memberships need no bookings.
                  { path: 'pricing-plans', element: lazily(<PricingPlansPage />) },
                  // The questions the storefront's FAQ sections answer, kept by
                  // the store for the same reason. Open to every plan.
                  { path: 'faqs', element: lazily(<FaqsPage />) },
                  // The roles the storefront's Careers page lists, kept by the
                  // store for the same reason. Open to every plan.
                  { path: 'careers', element: lazily(<CareersPage />) },
                  // Who applied through the storefront's apply form, and one of them.
                  { path: 'careers/applications', element: lazily(<ApplicationsPage />) },
                  { path: 'careers/applications/:id', element: lazily(<ApplicationDetailPage />) },
                  // Delivery pricing and the courier connection: a store setting
                  // like the gateway keys, and gated the same way.
                  {
                    element: <RequireEntitlement feature="SHIPPING_RATES" label="Shipping" />,
                    children: [
                      {
                        // Rates still save while Shiprocket is paused: a strip, not a wall.
                        element: <PausedFlagStrip flags={['SHIPPING_INTEGRATION']} />,
                        children: [{ path: 'shipping', element: lazily(<ShippingSettingsPage />) }],
                      },
                    ],
                  },
                  // What the shop says to its own customers, and which of those
                  // emails it sends at all. A storefront setting like the checkout
                  // options above it, and gated on the same permission.
                  {
                    element: (
                      <RequireEntitlement feature="CUSTOM_EMAIL_TEMPLATES" label="Custom email wording" />
                    ),
                    children: [{ path: 'email-templates', element: lazily(<EmailTemplatesPage />) }],
                  },
                  // Content has no page of its own: the sidebar's parent item is a
                  // door to the first thing behind it, not a screen to look at.
                  { path: 'content', element: <Navigate to="/app/content/pages" replace /> },
                  { path: 'content/pages', element: lazily(<PagesPage />) },
                  { path: 'content/pages/:pageId', element: lazily(<PageEditorPage />) },
                  {
                    element: <RequireEntitlement feature="BLOG" label="Blog" />,
                    children: [
                      { path: 'content/blog', element: lazily(<BlogPage />) },
                      // Before `blog/:postId` in spirit: the static segment wins the
                      // match, so `/app/content/blog/new` never resolves as a post id.
                      { path: 'content/blog/new', element: lazily(<BlogPostFormPage />) },
                      { path: 'content/blog/:postId', element: lazily(<BlogPostFormPage />) },
                    ],
                  },
                  {
                    element: <RequireEntitlement feature="METAOBJECTS" label="Custom content types" />,
                    children: [
                      { path: 'content/metaobjects', element: lazily(<MetaobjectsPage />) },
                      {
                        path: 'content/metaobjects/:definitionId',
                        element: lazily(<MetaobjectDefinitionPage />),
                      },
                      {
                        path: 'content/metaobjects/:definitionId/entries/:entryId',
                        element: lazily(<MetaobjectEntryPage />),
                      },
                      { path: 'content/metafields', element: lazily(<MetafieldsPage />) },
                    ],
                  },
                  { path: 'content/menus', element: lazily(<MenusPage />) },
                  // Before `menus/:menuId` in spirit: the static segment wins the
                  // match, so `/app/content/menus/new` never resolves as an id.
                  { path: 'content/menus/new', element: lazily(<MenuFormPage />) },
                  { path: 'content/menus/:menuId', element: lazily(<MenuFormPage />) },
                  // Multi-language: the overview, one entity's editor, and the
                  // store's language settings. See docs/i18n.md.
                  { path: 'content/translations', element: lazily(<TranslationsPage />) },
                  {
                    path: 'content/translations/:entityType/:entityId',
                    element: lazily(<TranslationEditorPage />),
                  },
                  { path: 'settings/languages', element: lazily(<LanguageSettingsPage />) },
                ],
              },
              {
                // Not gated on an entitlement: a merchant whose plan includes no
                // setup hours should still be able to open the screen and find out,
                // and be shown the way to a plan that does.
                element: <ProtectedRoute permission="SETUP_ASSISTANCE_REQUEST" />,
                children: [
                  { path: 'setup-assistance', element: lazily(<SetupAssistancePage />) },
                ],
              },
              {
                element: <RequireEntitlement feature="LIVE_SUPPORT_CHAT" label="Live support chat" />,
                children: [
                  {
                    element: <ProtectedRoute permission="SUPPORT_CHAT_READ" />,
                    children: [
                      { path: 'support', element: lazily(<SupportChatPage />) },
                      { path: 'support/:conversationId', element: lazily(<SupportChatPage />) },
                    ],
                  },
                ],
              },
              {
                element: <ProtectedRoute permission="BILLING_READ" />,
                children: [
                  { path: 'billing', element: lazily(<BillingPage />) },
                  // The payment step moved out of the dashboard to its own screen
                  // during sign-up. This path was documented, so it keeps working.
                  { path: 'billing/checkout', loader: subscribeRedirect },
                ],
              },
              {
                // USER_READ opens the screen; inviting, editing and deactivating are
                // each gated by their own permission on the API, and the page hides
                // the control it knows will be refused.
                element: <ProtectedRoute permission="USER_READ" />,
                children: [{ path: 'team', element: lazily(<TeamPage />) }],
              },
              {
                // Reading only. Saving needs TENANT_UPDATE, checked inside the
                // page and on the API, like the rest of the business profile.
                element: <ProtectedRoute permission="TENANT_READ" />,
                children: [
                  { path: 'settings', element: lazily(<SettingsPage />) },
                  { path: 'settings/signature', element: lazily(<SignatureSettingsPage />) },
                ],
              },
              // No permission: everyone manages their own sessions, and an account
              // holding no grants at all still needs to be able to lock itself down.
              { path: 'security', element: lazily(<SecurityPage />) },
              // Nor here: somebody's own name, email and password are theirs.
              // Reached by clicking your name in the top bar.
              { path: 'account', element: lazily(<MyAccountPage />) },
              // No permission either: every member may read the platform's terms.
              // Accepting them is refused by the API to anybody who does not sign
              // for the business, and the page hides the button for them.
              { path: 'policies', element: lazily(<MerchantPoliciesPage />) },
            ],
          },
        ],
      },
    ],
  },
  {
    // Platform staff. `requireBusiness={false}` because these accounts have no
    // business of their own - requiring one would bounce every one of them
    // straight into the merchant onboarding form.
    element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_MERCHANT_READ" />,
    children: [
      {
        // A catalog theme in the theme customiser: a workspace that owns the
        // whole window, so it sits outside the panel's chrome. The platform
        // owner's alone, like every other change to the catalog - the API
        // refuses everyone else's saves regardless.
        element: <ProtectedRoute requireBusiness={false} platformOwnerOnly />,
        children: [
          { path: 'platform/themes/:key/customize', element: lazily(<PlatformThemeCustomizerPage />) },
        ],
      },
      {
        path: 'platform',
        element: lazily(<PlatformLayout />),
        children: [
          { index: true, element: lazily(<PlatformOverviewPage />) },
          // Same reasoning as the merchant surface: the catalogue is filtered
          // per caller and each report re-checks its own permission, so this
          // route carries no guard beyond the platform one above it.
          {
            path: 'reports',
            children: [
              { index: true, element: lazily(<PlatformReportsPage />) },
              { path: ':reportKey', element: lazily(<PlatformReportPage />) },
            ],
          },
          {
            // Visitors' IP addresses and whereabouts: the platform owner's
            // alone, by role rather than by a permission that could be handed
            // on. The API refuses everyone else regardless.
            element: <ProtectedRoute requireBusiness={false} platformOwnerOnly />,
            children: [
              { path: 'site-visitors', element: lazily(<PlatformSiteVisitorsPage />) },
              // Versions, environment and dependency health: the owner's view
              // of the platform, moved here from the public `/status`.
              { path: 'status', element: lazily(<PlatformStatusPage />) },
              // The public site's content. Owner-only on the API as well.
              { path: 'site-content', element: lazily(<PlatformSiteContentPage />) },
            ],
          },
          { path: 'merchants', element: lazily(<MerchantsPage />) },
          { path: 'merchants/:tenantId', element: lazily(<MerchantDetailPage />) },
          { path: 'listings', element: lazily(<ModerationPage />) },
          { path: 'people', element: lazily(<PlatformUsersPage />) },
          {
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_ORDER_READ" />,
            children: [{ path: 'orders', element: lazily(<PlatformOrdersPage />) }],
          },
          {
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_PLAN_READ" />,
            children: [
              { path: 'plans', element: lazily(<PlansPage />) },
              // Reading only. Adding, editing or switching a language off needs
              // PLATFORM_PLAN_MANAGE, checked inside the page and on the API.
              { path: 'languages', element: lazily(<PlatformLanguagesPage />) },
              // Reading only. Adding, publishing or removing a picture needs
              // PLATFORM_PLAN_MANAGE, checked inside the page and on the API.
              { path: 'industry-images', element: lazily(<PlatformIndustryAssetsPage />) },
              // Reading only. Adding, publishing or removing a product needs
              // PLATFORM_PLAN_MANAGE, checked inside the page and on the API.
              { path: 'product-master', element: lazily(<PlatformProductMasterPage />) },
              // Reading only. Every change is the platform owner's, checked
              // inside the page (isPlatformOwner) and on the API.
              { path: 'themes', element: lazily(<PlatformThemesPage />) },
              // Reading only. Entering or correcting a bill needs
              // PLATFORM_PLAN_MANAGE, checked inside the page and on the API.
              { path: 'expenses', element: lazily(<PlatformExpensesPage />) },
            ],
          },
          {
            element: (
              <ProtectedRoute
                requireBusiness={false}
                permission="PLATFORM_SALES_INQUIRY_READ"
              />
            ),
            // Reading only. Deleting needs PLATFORM_SALES_INQUIRY_MANAGE,
            // checked inside the page and on the API.
            children: [
              { path: 'sales-inquiries', element: lazily(<PlatformSalesInquiriesPage />) },
            ],
          },
          {
            element: (
              <ProtectedRoute
                requireBusiness={false}
                permission="PLATFORM_SETUP_ASSIST_READ"
              />
            ),
            children: [
              { path: 'setup-assistance', element: lazily(<SetupAssistanceQueuePage />) },
              {
                path: 'setup-assistance/usage',
                element: lazily(<SetupAssistanceUsagePage />),
              },
            ],
          },
          {
            element: (
              <ProtectedRoute requireBusiness={false} permission="PLATFORM_SUPPORT_CHAT_READ" />
            ),
            children: [{ path: 'support-chat', element: lazily(<SupportChatInboxPage />) }],
          },
          {
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_STAFF_MANAGE" />,
            children: [{ path: 'staff', element: lazily(<PlatformStaffPage />) }],
          },
          {
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_AUDIT_READ" />,
            children: [{ path: 'audit', element: lazily(<PlatformAuditPage />) }],
          },
          {
            element: (
              <ProtectedRoute requireBusiness={false} permission="PLATFORM_IMPERSONATION_MANAGE" />
            ),
            children: [
              { path: 'support-sessions', element: lazily(<PlatformSupportSessionsPage />) },
            ],
          },
          {
            // Reading only. The replay control inside the page is gated
            // separately on PLATFORM_WEBHOOK_REPLAY, which support does not
            // hold - they need to see the failure, not act on it.
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_WEBHOOK_READ" />,
            children: [{ path: 'webhooks', element: lazily(<PlatformWebhooksPage />) }],
          },
          {
            // Reading only. The two overrides inside the page are gated
            // separately on PLATFORM_DOMAIN_MANAGE, which support does not hold.
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_DOMAIN_READ" />,
            children: [{ path: 'domains', element: lazily(<PlatformDomainsPage />) }],
          },
          {
            // Reading only. Flipping a switch needs PLATFORM_FLAG_MANAGE,
            // checked inside the page and again on the API.
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_FLAG_READ" />,
            children: [{ path: 'flags', element: lazily(<PlatformFlagsPage />) }],
          },
          {
            element: (
              <ProtectedRoute requireBusiness={false} permission="PLATFORM_INTEGRATION_READ" />
            ),
            children: [
              { path: 'integrations', element: lazily(<PlatformIntegrationsPage />) },
              // Reading only, like integrations - and the same staff already see
              // each merchant's AI spend there.
              { path: 'ai-expenses', element: lazily(<PlatformAIExpensesPage />) },
              // Reading only: switching a courier off for everyone needs
              // PLATFORM_FLAG_MANAGE, checked inside the page and on the API.
              { path: 'shipping', element: lazily(<PlatformShippingPage />) },
            ],
          },
          {
            // Reading only: the page changes nothing, it shows which merchants
            // have the channel and where it is failing them.
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_WHATSAPP_READ" />,
            children: [
              { path: 'whatsapp', element: lazily(<PlatformWhatsAppPage />) },
              { path: 'whatsapp/:tenantId', element: lazily(<PlatformWhatsAppPage />) },
            ],
          },
          {
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_PARTNER_READ" />,
            children: [
              { path: 'partners', element: lazily(<PartnersPage />) },
              { path: 'partners/:partnerId', element: lazily(<PartnerDetailPage />) },
              // The payout queue is a sibling of `partners`, not a child: as
              // `partners/commissions` it would shadow `partners/:partnerId`.
              { path: 'commissions', element: lazily(<PartnerCommissionsPage />) },
              // A sibling for the same reason; editing it needs PLATFORM_PARTNER_MANAGE,
              // checked inside the page and on the API.
              { path: 'commission-matrix', element: lazily(<CommissionMatrixPage />) },
              // Platform deals are codes owned by NeonCarts' house partner.
              { path: 'deals', element: lazily(<PlatformDealsPage />) },
            ],
          },
          {
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_JOB_READ" />,
            children: [{ path: 'ops', element: lazily(<PlatformOpsPage />) }],
          },
          {
            // Reading only. Changing a level - globally, per source, per
            // logger or for one merchant - needs PLATFORM_LOGS_MANAGE, checked
            // inside the page and again on the API.
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_LOGS_READ" />,
            children: [
              { path: 'system-logs', element: lazily(<PlatformSystemLogsPage />) },
              { path: 'log-levels', element: lazily(<PlatformLogLevelsPage />) },
            ],
          },
          {
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_COMMS_READ" />,
            children: [
              { path: 'emails', element: lazily(<PlatformEmailsPage />) },
              // Reading only; saving needs PLATFORM_COMMS_MANAGE, as templates do.
              { path: 'signature', element: lazily(<PlatformSignaturePage />) },
            ],
          },
          {
            // Reading only. Writing, publishing and disabling a policy need
            // PLATFORM_POLICY_MANAGE, checked inside the pages and on the API.
            element: <ProtectedRoute requireBusiness={false} permission="PLATFORM_POLICY_READ" />,
            children: [
              { path: 'policies', element: lazily(<PlatformPoliciesPage />) },
              { path: 'policies/:policyId', element: lazily(<PlatformPolicyDetailPage />) },
            ],
          },
        ],
      },
    ],
  },
  {
    // Partners. `requireBusiness={false}` for the same reason platform staff
    // need it: a partner introduces businesses without belonging to one, so
    // the merchant onboarding guard would bounce them out of their own portal.
    element: <ProtectedRoute requireBusiness={false} permission="PARTNER_PORTAL" />,
    children: [
      {
        path: 'partner',
        element: lazily(<PartnerLayout />),
        children: [
          { index: true, element: lazily(<PartnerDashboardPage />) },
          { path: 'referrals', element: lazily(<PartnerReferralsPage />) },
          { path: 'earnings', element: lazily(<PartnerEarningsPage />) },
          { path: 'commission', element: lazily(<PartnerCommissionPage />) },
          { path: 'profile', element: lazily(<PartnerProfilePage />) },
        ],
      },
    ],
  },
  // The shop, twice: in its default language at `/store/...` - every address
  // that existed before languages did - and in any other at `/store/<code>/...`
  // (docs/i18n.md §9). The same children under both, built fresh per mount.
  // Static segments outrank `:lang`, so `/store/cart` is the cart; a `:lang`
  // that is not a language the shop publishes is the layout's to turn away.
  //
  // And the whole of it again under `/preview/store`, where the dashboard's
  // "View store" links point: an address no installed shop app claims, so a
  // merchant's preview opens in a tab rather than in whichever shop they
  // installed first (see `PREVIEW_STORE_ROOT`).
  ...[LIVE_STORE_ROOT, PREVIEW_STORE_ROOT].flatMap((root) =>
    [root, `${root}/:lang`].map((path) => ({ root, path: path.slice(1) })),
  ).map(({ root, path }): RouteObject => ({
    path,
    element: lazily(<StorefrontLayout root={root} />),
    children: [
      { index: true, element: lazily(<StorefrontHomePage />) },
      // The catalog: what "All products" menu links, the default hero button
      // and every category card point at.
      { path: 'products', element: lazily(<StorefrontProductsPage />) },
      { path: 'products/:slug', element: lazily(<StorefrontProductPage />) },
      // Browsing by department. The index is a page of its own because a
      // shopper who does not yet know what they want needs one; a single
      // shelf is the catalog filtered, served by the catalog page rather than
      // by a second copy of it.
      { path: 'categories', element: lazily(<StorefrontCategoriesPage />) },
      { path: 'categories/:slug', element: lazily(<StorefrontProductsPage />) },
      // Search and faceted filtering. Its own address rather than a mode of
      // the catalog page: a narrowed result set is a thing shoppers paste to
      // each other, and the query string is the whole of its state.
      {
        element: <RequireFeature feature="search" />,
        children: [{ path: 'search', element: lazily(<StorefrontSearchPage />) }],
      },
      // The merchant's own pages and writing. `/pages/…` and `/blogs/…` are the
      // addresses menus, emails and search results already point at, so they
      // are the addresses these serve.
      { path: 'pages/:handle', element: lazily(<StorefrontPageView />) },
      // One person on a service business's team - the page a team card's
      // name opens. Not behind a feature: a shop with nobody shows a 404.
      { path: 'team/:slug', element: lazily(<StorefrontTeamMemberPage />) },
      // The shop's open positions and each role's own page. Behind the
      // careers switch, which starts off: a shop that has not turned it on
      // has no careers page, and these addresses are its 404.
      {
        element: <RequireFeature feature="careers" />,
        children: [
          { path: 'careers', element: lazily(<StorefrontCareersPage />) },
          { path: 'careers/:slug', element: lazily(<StorefrontCareerPage />) },
        ],
      },
      // The token queue's public "Now serving" board (and, with `?tv=1`, the
      // waiting-room screen). Numbers only; a shop with no token queues says so.
      { path: 'queue', element: lazily(<StorefrontQueuePage />) },
      // A NeonCarts policy a shop falls back on in its footer, in the shop's
      // own chrome. See `StorefrontPolicyLinks`.
      { path: 'policies/:slug', element: lazily(<StorefrontPolicyPage />) },
      {
        element: <RequireFeature feature="blog" />,
        children: [
          { path: 'blogs/:blogHandle', element: lazily(<StorefrontBlogPage />) },
          { path: 'blogs/:blogHandle/:postHandle', element: lazily(<StorefrontPostPage />) },
        ],
      },
      // Two gates rather than one: a shop may keep a basket and take the till
      // away - "collect what you want, then telephone us" is a real way for a
      // service business to work.
      {
        element: <RequireFeature feature="cart" />,
        children: [{ path: 'cart', element: lazily(<CartPage />) }],
      },
      // The wishlist plugin's page. Ungated here: the page itself is the
      // shop's 404 while the plugin is off.
      { path: 'wishlist', element: lazily(<StorefrontWishlistPage />) },
      {
        element: <RequireFeature feature="checkout" />,
        children: [{ path: 'checkout', element: lazily(<CheckoutPage />) }],
      },
      // Where checkout lands. An address of its own rather than a state inside
      // the form: a shopper who reloads should not be looking at a checkout
      // for a basket that has already been spent.
      //
      // Deliberately not behind the checkout gate. It is a receipt - nothing on
      // it can be bought - and an online payment confirms on a webhook, so
      // gating it would strand a shopper who has already paid if the merchant
      // switched the till off in between. Once menus are filtered, nothing
      // links here anyway.
      { path: 'order/:orderNumber', element: lazily(<OrderConfirmationPage />) },
      // An order placed in a WhatsApp chat, paid on the web: the link the bot
      // sends carries a token that proves the shopper was handed it. Outside
      // the checkout gate for the same reason as the receipt above - the
      // order already exists, and the gateway confirms on a webhook.
      { path: 'orders/:orderId/pay', element: lazily(<OrderPayPage />) },

      // The shopper's own account. The signed-out pages sit beside the guarded
      // ones rather than under a separate prefix, so the links in a
      // confirmation email ("/account/verify-email") read the way a shopper
      // would expect and survive the redirect into sign-in and back out.
      {
        element: <RequireFeature feature="accounts" />,
        children: [
          { path: 'account/login', element: lazily(<CustomerLoginPage />) },
          { path: 'account/register', element: lazily(<CustomerRegisterPage />) },
          { path: 'account/forgot-password', element: lazily(<CustomerForgotPasswordPage />) },
          { path: 'account/reset-password', element: lazily(<CustomerResetPasswordPage />) },
          { path: 'account/verify-email', element: lazily(<CustomerVerifyEmailPage />) },
          {
            element: <RequireCustomer />,
            children: [
              {
                path: 'account',
                element: lazily(<AccountLayout />),
                children: [
                  { index: true, element: lazily(<AccountOverviewPage />) },
                  { path: 'orders', element: lazily(<AccountOrdersPage />) },
                  { path: 'bookings', element: lazily(<AccountBookingsPage />) },
                  { path: 'packages', element: lazily(<AccountPackagesPage />) },
                  { path: 'orders/:orderId', element: lazily(<AccountOrderDetailPage />) },
                  { path: 'addresses', element: lazily(<AccountAddressesPage />) },
                  { path: 'profile', element: lazily(<AccountProfilePage />) },
                ],
              },
            ],
          },
        ],
      },
      // The shop's own dead end, inside the shop's own layout. Without this a
      // mistyped address on a merchant's domain falls through to the
      // platform's 404 - a page with none of their chrome and no way back into
      // their catalog.
      { path: '*', element: lazily(<StorefrontNotFoundPage />) },
    ],
  })),
  // Themes: the gallery, and each theme running as a complete sample shop.
  // Its own tree beside `/store` rather than inside it, because these pages
  // belong to a theme rather than to a merchant - which is what makes them the
  // thing to show somebody who has not built a shop yet.
  ...PREVIEW_ROUTES,
  { path: 'account/*', loader: storeAccountRedirect, element: <NotFoundPage /> },
  { path: '*', element: <NotFoundPage /> },
];

/**
 * A pathless route above every surface, so the scroll reset applies to the
 * marketing site, auth pages, dashboard, storefront and the 404 alike. The
 * analytics pieces decide for themselves to stay silent on a shop.
 */
export const router = createBrowserRouter([
  {
    element: (
      <>
        <ScrollToTop />
        <AnalyticsTracker />
        <Outlet />
        <ConsentBanner />
      </>
    ),
    // One screen for every route's failure; a page whose code went stale under
    // a deploy or a dev reload is reloaded rather than reported.
    errorElement: <RouteError />,
    children: ROUTES,
  },
]);
